
import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Users, TrendingUp, Repeat, PercentIcon, DollarSign, BadgeDollarSign, ArrowDownCircle } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import TechBackground from '@/components/ui/TechBackground';

const KeyMetrics = () => {
  const scrollToContact = () => {
    const section = document.getElementById('contact');
    if (section) {
      const navbarHeight = 64; // Consistent with the navbar height
      
      // Find the emoji element within the section
      const emojiElement = section.querySelector('.text-xl, .text-2xl');
      
      if (emojiElement) {
        // Calculate position of the emoji element
        const emojiPosition = emojiElement.getBoundingClientRect().top + window.pageYOffset;
        
        // Add a small offset for better positioning
        const scrollPosition = emojiPosition - navbarHeight - 20;
        
        window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      } else {
        // Fallback to the section top if emoji not found
        const offsetTop = section.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    }
  };

  // eCommerce KPI metrics with specific numbers and descriptions
  const ecommerceMetrics = [
    {
      icon: <DollarSign className="w-4 h-4 text-white" />,
      value: "35%",
      label: "Higher Average Order Value",
      description: "Better than industry average",
      iconBg: "bg-gradient-to-br from-emerald-500 to-emerald-700",
      textColor: "text-emerald-400",
      borderColor: "border-emerald-500/30"
    },
    {
      icon: <PercentIcon className="w-4 h-4 text-white" />,
      value: "4.2%",
      label: "Better Conversion Rate",
      description: "Above retail benchmark",
      iconBg: "bg-gradient-to-br from-blue-500 to-blue-700",
      textColor: "text-blue-400",
      borderColor: "border-blue-500/30"
    },
    {
      icon: <ShoppingCart className="w-4 h-4 text-white" />,
      value: "28%",
      label: "Lower Cart Abandonment",
      description: "Below industry average",
      iconBg: "bg-gradient-to-br from-amber-500 to-amber-700",
      textColor: "text-amber-400",
      borderColor: "border-amber-500/30"
    },
    {
      icon: <Repeat className="w-4 h-4 text-white" />,
      value: "65%",
      label: "Strong Repeat Purchase Rate",
      description: "Creating loyal customers who return",
      iconBg: "bg-gradient-to-br from-rose-500 to-rose-700",
      textColor: "text-rose-400",
      borderColor: "border-rose-500/30"
    },
    {
      icon: <Users className="w-4 h-4 text-white" />,
      value: "40%",
      label: "Reduced Customer Acquisition Cost",
      description: "Optimized for better ROI",
      iconBg: "bg-gradient-to-br from-indigo-500 to-indigo-700",
      textColor: "text-indigo-400",
      borderColor: "border-indigo-500/30"
    },
    {
      icon: <BadgeDollarSign className="w-4 h-4 text-white" />,
      value: "3.5x",
      label: "Improved Return on Ad Spend",
      description: "Better results for your ad budget",
      iconBg: "bg-gradient-to-br from-purple-500 to-purple-700",
      textColor: "text-purple-400",
      borderColor: "border-purple-500/30"
    },
  ];

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <section className="py-12 md:py-16" id="metrics">
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <SectionHeader
            title="KPI Metrics"
            subtitle="Our clients outperform the industry with real results"
            emoji="📊"
            titleColor="text-white"
          />
        </motion.div>
        
        <div className="mt-8 md:mt-12">
          <TechBackground variant="default" glow="red" className="p-6 md:p-8">
            <TooltipProvider>
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-6xl mx-auto"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
              >
                {ecommerceMetrics.map((metric, index) => (
                  <motion.div 
                    key={index}
                    variants={itemVariants}
                    whileHover={{ 
                      scale: 1.02, 
                      transition: { duration: 0.2 } 
                    }}
                    className="relative"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`bg-white/5 backdrop-blur-sm shadow-lg border ${metric.borderColor} rounded-lg p-4 h-full overflow-hidden`}>
                          {/* Tech-style glowing border */}
                          <div className="absolute inset-0 rounded-lg border border-white/10 backdrop-blur-sm pointer-events-none"></div>
                          
                          {/* Content container */}
                          <div className="relative z-10">
                            {/* Compact layout with horizontal alignment */}
                            <div className="flex items-center gap-3">
                              <div className={`${metric.iconBg} w-10 h-10 rounded-lg flex items-center justify-center shadow-lg border border-white/10`}>
                                {metric.icon}
                              </div>
                              <div>
                                <div className="flex items-baseline gap-2">
                                  <h3 className={`text-2xl font-bold ${metric.textColor}`}>
                                    {metric.value}
                                  </h3>
                                  <p className="text-sm text-gray-300">
                                    {metric.description}
                                  </p>
                                </div>
                                <p className="font-bold text-white text-sm">
                                  {metric.label}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-900 border-gray-800 text-white">
                        <p className="text-sm font-bold">{metric.label}: {metric.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </motion.div>
                ))}
              </motion.div>
            </TooltipProvider>
          </TechBackground>
        </div>
        
        {/* Add a transition to the contact section */}
        <motion.div 
          className="flex justify-center mt-10"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex flex-col items-center">
            <p className="text-white/80 text-center mb-4 max-w-lg mx-auto">
              Ready to achieve these results for your business?
            </p>
            <button
              onClick={scrollToContact}
              className="group flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg px-4 py-2 transition-all duration-300"
            >
              <span className="text-white font-medium">Get started now</span>
              <motion.div
                animate={{ y: [0, 4, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <ArrowDownCircle className="w-5 h-5 text-[#FF6B6B] group-hover:text-white transition-colors" />
              </motion.div>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default KeyMetrics;
